import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs, deleteDoc, doc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../Shared/Sidebar';

export default function Parents() {
  const [parents, setParents] = useState([]);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    fetchParents();
  }, []);

  async function fetchParents() {
    try {
      const [snap, studentSnap] = await Promise.all([
        getDocs(collection(db, 'parents')),
        getDocs(collection(db, 'students')),
      ]);
      setParents(
        snap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
      setStudents(
        studentSnap.docs.map(d => ({ id: d.id, ...d.data() }))
      );
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  // Link parent -> children: match by phone number (normalized), falling
  // back to the parent's full name as entered on the student form.
  function childrenOf(p) {
    // Prefer the explicit link set on the parent form — reliable and
    // works even if phone numbers were typed differently on each form.
    if (p.linkedChildren && p.linkedChildren.length > 0) {
      return p.linkedChildren
        .map(link => students.find(st => st.id === link.id))
        .filter(Boolean);
    }
    // Fallback for older records saved before linking existed.
    const norm = t => (t || '').replace(/\D/g, '').slice(-9);
    const pPhone = norm(p.phone);
    const pName = `${p.firstName || ''} ${p.lastName || ''}`.trim().toLowerCase();
    return students.filter(st => {
      const sPhone = norm(st.parentPhone);
      if (pPhone && sPhone && pPhone === sPhone) return true;
      const sName = (st.parentName || '').trim().toLowerCase();
      return pName && sName && sName === pName;
    });
  }

  async function handleDelete(id) {
    if (window.confirm(
      'Are you sure you want to remove this parent?'
    )) {
      await deleteDoc(doc(db, 'parents', id));
      fetchParents();
    }
  }

  const filtered = parents.filter(p => {
    const matchSearch =
      `${p.firstName} ${p.lastName} ${p.parentId} ${p.region} ${p.businessType}`
        .toLowerCase()
        .includes(search.toLowerCase());

    const matchFilter =
      filter === 'all' ? true :
      filter === 'microfinance'
        ? p.microfinance?.status === 'Active' || p.capitalAmount
        : filter === 'noCert'
        ? p.hasBirthCertificate === 'no'
        : true;

    return matchSearch && matchFilter;
  });

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>Parents</div>
            <div style={styles.pageSub}>
              All enrolled parents and guardians
            </div>
          </div>
          <button
            style={styles.btnPrimary}
            onClick={() => navigate('/staff/add-parent')}
          >
            + Add Parent
          </button>
        </div>

        {/* SEARCH */}
        <div style={styles.searchBar}>
          <span style={styles.searchIcon}></span>
          <input
            style={styles.searchInput}
            placeholder="Search by name, ID, region or business..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* STATS ROW */}
        <div style={styles.statsRow}>
          <div style={styles.statPill}>
            <div style={{
              ...styles.statPillNum, color: '#14452F'
            }}>
              {parents.length}
            </div>
            <div style={styles.statPillLabel}>
              Total Parents
            </div>
          </div>
          <div style={styles.statPill}>
            <div style={{
              ...styles.statPillNum, color: '#E65100'
            }}>
              {parents.filter(p =>
                p.microfinance?.status === 'Active' ||
                p.capitalAmount
              ).length}
            </div>
            <div style={styles.statPillLabel}>
              Microfinance
            </div>
          </div>
          <div style={styles.statPill}>
            <div style={{
              ...styles.statPillNum, color: '#4527A0'
            }}>
              {parents.filter(p =>
                p.sessions?.raSession1 ||
                p.sessions?.raSession2
              ).length}
            </div>
            <div style={styles.statPillLabel}>
              RA Sessions
            </div>
          </div>
          <div style={styles.statPill}>
            <div style={{
              ...styles.statPillNum, color: '#C62828'
            }}>
              {parents.filter(p =>
                p.hasBirthCertificate === 'no'
              ).length}
            </div>
            <div style={styles.statPillLabel}>
              Need Certificate
            </div>
          </div>
        </div>

        {/* FILTERS */}
        <div style={styles.filterRow}>
          {[
            { id: 'all', label: 'All' },
            { id: 'microfinance', label: 'Microfinance' },
            { id: 'noCert', label: 'Need Certificate' },
          ].map(f => (
            <button
              key={f.id}
              style={{
                ...styles.filterBtn,
                ...(filter === f.id
                  ? styles.filterBtnActive : {})
              }}
              onClick={() => setFilter(f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>

        {/* PARENTS LIST */}
        <div style={styles.panel}>
          {loading ? (
            <div style={styles.empty}>
              Loading parents...
            </div>
          ) : filtered.length === 0 ? (
            <div style={styles.empty}>
              {search
                ? 'No parents match your search.'
                : 'No parents yet. Add your first parent!'}
            </div>
          ) : (
            filtered.map((p) => (
              <div key={p.id} style={styles.parentRow}>

                {/* AVATAR */}
                <div style={styles.avatar}>
                  {p.photoURL ? (
                    <img
                      src={p.photoURL}
                      alt={p.firstName}
                      style={styles.avatarImg}
                    />
                  ) : (
                    <span>
                      {p.firstName?.[0]}{p.lastName?.[0]}
                    </span>
                  )}
                </div>

                {/* INFO */}
                <div style={styles.parentInfo}>
                  <div style={styles.parentName}>
                    {p.firstName} {p.lastName}
                  </div>
                  <div style={styles.parentMeta}>
                    🪪 {p.parentId} · {p.region} ·
                    {p.phone}
                  </div>
                  <div style={styles.parentMeta}>
                    {p.numberOfChildren || 0} children ·
                    {p.businessType || 'No business yet'}
                  </div>
                </div>

                {/* BADGES */}
                <div style={styles.badgeCol}>
                  <span style={{
                    ...styles.badge,
                    background:
                      p.hasBirthCertificate === 'yes'
                        ? '#EAF3EC'
                        : p.hasBirthCertificate === 'processing'
                        ? '#FFF3E0'
                        : '#FFEBEE',
                    color:
                      p.hasBirthCertificate === 'yes'
                        ? '#14452F'
                        : p.hasBirthCertificate === 'processing'
                        ? '#E65100'
                        : '#C62828',
                  }}>
                    {p.hasBirthCertificate === 'yes'
                      ? 'Has Certificate'
                      : p.hasBirthCertificate === 'processing'
                      ? 'Processing'
                      : 'No Certificate'}
                  </span>

                  {p.capitalAmount && (
                    <span style={styles.badgeGreen}>
                      Microfinance
                    </span>
                  )}

                  {(p.sessions?.raSession1 ||
                    p.sessions?.raSession2) && (
                    <span style={styles.badgePurple}>
                      RA Sessions
                    </span>
                  )}
                </div>

                {/* STORY */}
                <div style={styles.storyIndicator}>
                  {p.familyStory?.before ? (
                    <span style={styles.hasStory}>
                      Has Story
                    </span>
                  ) : (
                    <span style={styles.noStory}>
                      No Story Yet
                    </span>
                  )}
                </div>

                {/* CHILDREN SUPPORTED BY THE FOUNDATION */}
                {(() => {
                  const kids = childrenOf(p);
                  if (kids.length === 0) return null;
                  return (
                    <div style={styles.childrenBox}>
                      <div style={styles.childrenLabel}>
                        Watoto wanaosaidiwa na Foundation ({kids.length})
                      </div>
                      <div style={styles.childrenList}>
                        {kids.map(k => (
                          <button
                            key={k.id}
                            style={styles.childChip}
                            onClick={() => navigate(`/admin/students`)}
                            title="View in Students"
                          >
                            {k.firstName} {k.lastName} · {k.class || k.school || 'Student'}
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}

                {/* ACTIONS */}
                <div style={styles.actions}>
                  <button
                    style={styles.btnEdit}
                    onClick={() => navigate(
                      `/edit-parent/${p.id}`
                    )}
                  >
                    Edit
                  </button>
                  <button
                    style={styles.btnDelete}
                    onClick={() => handleDelete(p.id)}
                  >
                    Futa
                  </button>
                </div>

              </div>
            ))
          )}
        </div>

      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: '24px',
    flexWrap: 'wrap',
    gap: '14px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  btnPrimary: {
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    color: '#fff',
    border: 'none',
    padding: '10px 20px',
    borderRadius: '10px',
    fontFamily: 'inherit',
    fontSize: '13px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 4px 14px rgba(245,124,0,0.25)',
  },
  searchBar: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    background: '#fff',
    borderRadius: '12px',
    padding: '10px 16px',
    marginBottom: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  searchIcon: { fontSize: '16px' },
  searchInput: {
    flex: 1,
    border: 'none',
    outline: 'none',
    fontSize: '13.5px',
    fontFamily: 'inherit',
    background: 'transparent',
    color: '#1C1C1E',
  },
  statsRow: {
    display: 'flex',
    gap: '12px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  statPill: {
    background: '#fff',
    borderRadius: '12px',
    padding: '12px 18px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    textAlign: 'center',
    minWidth: '80px',
  },
  statPillNum: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '22px',
    fontWeight: '700',
  },
  statPillLabel: {
    fontSize: '11px',
    color: '#6B7280',
    marginTop: '2px',
  },
  filterRow: {
    display: 'flex',
    gap: '8px',
    marginBottom: '16px',
    flexWrap: 'wrap',
  },
  filterBtn: {
    padding: '8px 16px',
    borderRadius: '20px',
    border: '1.5px solid #E5E7EB',
    background: 'transparent',
    color: '#6B7280',
    fontSize: '12.5px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  filterBtnActive: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    borderColor: 'transparent',
    color: '#fff',
  },
  panel: {
    background: '#fff',
    borderRadius: '16px',
    padding: '8px 20px',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  empty: {
    textAlign: 'center',
    color: '#6B7280',
    fontSize: '13px',
    padding: '30px 0',
  },
  parentRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '14px',
    padding: '14px 0',
    borderBottom: '1px solid #F3F4F6',
    flexWrap: 'wrap',
  },
  avatar: {
    width: '44px',
    height: '44px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #F57C00, #FFA726)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '14px',
    fontWeight: '600',
    color: '#fff',
    flexShrink: 0,
    overflow: 'hidden',
  },
  avatarImg: {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
  parentInfo: { flex: 1, minWidth: '200px' },
  parentName: {
    fontSize: '14px',
    fontWeight: '600',
    marginBottom: '3px',
  },
  parentMeta: {
    fontSize: '11.5px',
    color: '#6B7280',
    marginTop: '2px',
    lineHeight: 1.5,
  },
  childrenBox: { background: 'rgba(20,69,47,0.05)', border: '1px solid rgba(20,69,47,0.12)', borderRadius: '10px', padding: '10px 12px', marginBottom: '12px' },
  childrenLabel: { fontSize: '11px', fontWeight: '700', color: '#14452F', marginBottom: '8px' },
  childrenList: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
  childChip: { background: '#fff', border: '1px solid rgba(20,69,47,0.25)', color: '#14452F', fontSize: '11.5px', fontWeight: '600', padding: '5px 10px', borderRadius: '20px', cursor: 'pointer', fontFamily: 'inherit' },
  badgeCol: {
    display: 'flex',
    flexDirection: 'column',
    gap: '5px',
  },
  badge: {
    fontSize: '10.5px',
    padding: '3px 9px',
    borderRadius: '20px',
    fontWeight: '500',
    display: 'inline-block',
  },
  badgeGreen: {
    fontSize: '10.5px',
    padding: '3px 9px',
    borderRadius: '20px',
    fontWeight: '500',
    background: '#EAF3EC',
    color: '#14452F',
    display: 'inline-block',
  },
  badgePurple: {
    fontSize: '10.5px',
    padding: '3px 9px',
    borderRadius: '20px',
    fontWeight: '500',
    background: '#EDE7F6',
    color: '#4527A0',
    display: 'inline-block',
  },
  storyIndicator: { textAlign: 'center' },
  hasStory: {
    fontSize: '10.5px',
    padding: '3px 9px',
    borderRadius: '20px',
    background: '#E3F2FD',
    color: '#1565C0',
    fontWeight: '500',
  },
  noStory: {
    fontSize: '10.5px',
    padding: '3px 9px',
    borderRadius: '20px',
    background: '#F3F4F6',
    color: '#6B7280',
    fontWeight: '500',
  },
  actions: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  btnEdit: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    padding: '7px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  btnDelete: {
    background: '#FFEBEE',
    color: '#C62828',
    border: 'none',
    padding: '7px 10px',
    borderRadius: '8px',
    fontSize: '14px',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};